import {
  ORDER_SERIES_BUCKETS,
  skuKeyLabel,
  storeDate,
  type SkuSeries,
} from "@estelle/domain";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { ForbiddenError } from "@/adapters/api/base";
import { useDataSource } from "@/adapters/data-source";
import { ChartCard, FigureTable } from "@/components/charts/ChartCard";
import { MultiLineChart, type LineSeries } from "@/components/charts/lazy";
import {
  EmptyState,
  LoadFailed,
  NoAccess,
} from "@/components/shell/EmptyState";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCompactMoney, formatCount } from "@/lib/chart-format";
import { reportingNow } from "@/lib/reportClock";
import { cn } from "@/lib/utils";
import { formatMoney } from "@/modules/gst/format";
import { DateRangeControls } from "@/modules/reports/DateRangeControls";
import {
  bucketDetail,
  bucketLabel,
  rangeLabel,
} from "@/modules/reports/orderChart";
import { ProductPicker } from "@/modules/reports/screens/ProductPicker";
import { ShareOfTotal } from "@/modules/reports/screens/ShareOfTotal";
import { ProductViews } from "@/modules/reports/screens/ProductViews";
import { ReportHeader } from "@/modules/reports/ReportHeader";
import { Segmented } from "@/modules/reports/Segmented";
import {
  SKU_TABLE_ORDERS,
  TREND_PRESETS,
  orderSkuItems,
  pickColour,
  productsRange,
  shareText,
  skuLabel,
  trendPresetRange,
  type ProductsState,
  type SkuTableOrder,
} from "@/modules/reports/skuTrends";

const BUCKET_LABELS = { day: "Day", week: "Week", month: "Month" } as const;

/** How many SKUs the table shows before "Show all". */
const TABLE_ROWS = 25;

/**
 * Units and sales per SKU over time (#678): Reports > Products' second view.
 *
 * The dates sit at the top, with the three quick ranges people reach for and
 * the picker for any other. Under them, which products to draw — the five best
 * sellers until someone searches for and picks their own — then two line
 * charts on the same dates, units sold and sales amount, and a table of every
 * SKU that sold in the range. All of it is one answer, `orders.skuSeries`,
 * counted where the order lines are, in the store's timezone: no line is
 * downloaded to be added up here. The settings live in the URL beside the other
 * reports', so a view can be linked to.
 */
export function SkuTrends({
  state,
  change,
}: {
  state: ProductsState;
  change: (patch: Partial<ProductsState>) => void;
}) {
  const data = useDataSource();
  const today = storeDate(reportingNow());
  const range = productsRange(state, today);

  const presets = useMemo(
    () =>
      TREND_PRESETS.map((preset) => ({
        ...preset,
        ...trendPresetRange(preset.id, today),
      })),
    [today],
  );

  const request = {
    from: range.from,
    to: range.to,
    bucket: state.bucket,
    includeCancelled: state.includeCancelled,
    ...(state.picks.length > 0 ? { keys: state.picks } : {}),
  };

  const query = useQuery({
    queryKey: ["reports", "sku-series", request],
    queryFn: () => data.orders.skuSeries(request),
    // Keep the last charts on screen, dimmed, while the next are counted.
    placeholderData: keepPreviousData,
  });

  const series: SkuSeries | undefined = query.data;
  const rangeText = rangeLabel(
    series?.from ?? range.from,
    series?.to ?? range.to,
  );

  if (query.error instanceof ForbiddenError) {
    return (
      <NoAccess
        permission={query.error.permission ?? "reports.products.view"}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <ReportHeader
        readAt={series ? query.dataUpdatedAt : undefined}
        controls={
          <section aria-label="Report dates">
            <DateRangeControls
              idPrefix="sku-trends"
              range={range}
              today={today}
              presets={presets}
              onChange={(next) => {
                change({ range: next });
              }}
              align="end"
            />
          </section>
        }
      />
      <ProductViews state={state} change={change} />

      <section aria-label="Chart settings" className="flex flex-col gap-4">
        {series ? (
          <ProductPicker
            items={series.items}
            picked={series.picked}
            isDefault={state.picks.length === 0}
            onChange={(picks) => {
              change({ picks });
            }}
            onReset={() => {
              change({ picks: [] });
            }}
          />
        ) : null}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <Segmented
            label="Group by"
            value={state.bucket}
            options={ORDER_SERIES_BUCKETS.map((bucket) => ({
              value: bucket,
              label: BUCKET_LABELS[bucket],
            }))}
            onChange={(bucket) => {
              change({ bucket });
            }}
          />
          <div className="flex items-center gap-2">
            <Checkbox
              id="sku-trends-cancelled"
              checked={state.includeCancelled}
              onCheckedChange={(checked) => {
                change({ includeCancelled: checked === true });
              }}
            />
            <Label
              htmlFor="sku-trends-cancelled"
              className="text-sm font-normal"
            >
              Include cancelled orders
            </Label>
          </div>
        </div>
      </section>

      {query.isError ? (
        <LoadFailed
          message={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : !series ? (
        <div className="flex flex-col gap-6">
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-80 w-full" />
        </div>
      ) : series.items.length === 0 ? (
        <EmptyState
          title="No sales in these dates"
          description={`Nothing was sold between ${rangeText}.${state.includeCancelled ? "" : " Cancelled orders are left out."}`}
        />
      ) : (
        <div
          aria-busy={query.isPlaceholderData}
          className={cn(
            "flex flex-col gap-6 transition-opacity",
            query.isPlaceholderData && "opacity-50",
          )}
        >
          <SkuCharts series={series} state={state} rangeText={rangeText} />
          <ShareOfTotal series={series} />
          <SkuTable series={series} />
        </div>
      )}
    </div>
  );
}

function SkuCharts({
  series,
  state,
  rangeText,
}: {
  series: SkuSeries;
  state: ProductsState;
  rangeText: string;
}) {
  const names = useMemo(
    () =>
      new Map(series.items.map((item) => [item.key, skuLabel(item)] as const)),
    [series.items],
  );

  const toLines = (
    lines: SkuSeries["units"],
    note: (total: string) => string,
  ): LineSeries[] =>
    lines.map((line, index) => ({
      id: line.key,
      label: names.get(line.key) ?? skuKeyLabel(line.key),
      values: line.values.map(Number),
      color: pickColour(index),
      note: note(line.total),
    }));

  const unitLines = toLines(series.units, (total) =>
    formatCount(Number(total)),
  );
  const amountLines = toLines(series.amount, (total) => formatMoney(total));

  const labels = series.buckets.map((date) => bucketLabel(date, series.bucket));
  const details = series.buckets.map((date) =>
    bucketDetail(date, series.bucket),
  );

  const drawn = state.picks.length > 0 ? "chosen" : "best-selling";
  const scope = `${rangeText}, by ${series.bucket}. ${String(series.picked.length)} ${drawn} of ${String(series.items.length)} SKUs that sold.`;
  const basis = (
    <span data-testid="chart-basis" className="mt-1 block">
      Units and sales are as placed: refunds and removals are not deducted.{" "}
      {state.includeCancelled
        ? "Cancelled orders are included."
        : "Cancelled orders are left out; turn on “Include cancelled orders” to count them."}
    </span>
  );

  const total = (lines: LineSeries[]) =>
    lines.reduce(
      (sum, line) => sum + line.values.reduce((a, value) => a + value, 0),
      0,
    );
  const formatUnits = (value: number) => formatCount(value);
  const formatAmount = (value: number) => formatMoney(value.toFixed(2));

  function table(lines: LineSeries[], format: (value: number) => string) {
    return (
      <FigureTable
        rows={series.buckets}
        rowKey={(date) => date}
        columns={[
          {
            header: BUCKET_LABELS[series.bucket],
            cell: (date) => bucketDetail(date, series.bucket),
          },
          ...lines.map((line) => ({
            header: line.label,
            numeric: true,
            cell: (_: string, index: number) => format(line.values[index] ?? 0),
          })),
        ]}
      />
    );
  }

  return (
    <>
      <ChartCard
        title="Units sold over time"
        description={
          <>
            {formatCount(total(unitLines))} units on the chart · {scope}
            {basis}
          </>
        }
        table={table(unitLines, formatUnits)}
      >
        <MultiLineChart
          labels={labels}
          detailLabels={details}
          series={unitLines}
          ariaLabel={`Units sold per ${series.bucket} for ${unitLines.map((line) => line.label).join(", ")}, ${rangeText}.`}
          formatValue={formatUnits}
          formatTick={formatCount}
        />
      </ChartCard>
      <ChartCard
        title="Sales amount over time"
        description={
          <>
            {formatAmount(total(amountLines))} on the chart · {scope}
          </>
        }
        table={table(amountLines, formatAmount)}
      >
        <MultiLineChart
          labels={labels}
          detailLabels={details}
          series={amountLines}
          ariaLabel={`Sales amount per ${series.bucket} for ${amountLines.map((line) => line.label).join(", ")}, ${rangeText}.`}
          formatValue={formatAmount}
          formatTick={formatCompactMoney}
        />
      </ChartCard>
    </>
  );
}

function SkuTable({ series }: { series: SkuSeries }) {
  const [by, setBy] = useState<SkuTableOrder>("units");
  const [all, setAll] = useState(false);

  const rows = useMemo(
    () => orderSkuItems(series.items, by),
    [series.items, by],
  );
  const shown = all ? rows : rows.slice(0, TABLE_ROWS);
  const slot = new Map(series.picked.map((key, index) => [key, index]));

  return (
    <Card className="gap-4">
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-base leading-snug font-semibold">
              Every SKU in these dates
            </h3>
            <p className="text-muted-foreground text-sm">
              {formatCount(series.items.length)} SKUs sold, with{" "}
              {formatCount(series.totals.units)} units and{" "}
              {formatMoney(series.totals.amount)} in all. Those on the charts
              carry their colour.
            </p>
          </div>
          <Segmented
            label="Order table by"
            value={by}
            options={SKU_TABLE_ORDERS}
            onChange={setBy}
          />
        </div>
        <div className="overflow-x-auto">
          <FigureTable
            rows={shown}
            rowKey={(row) => row.key}
            columns={[
              {
                header: "SKU",
                strong: true,
                cell: (row) => {
                  const place = slot.get(row.key);
                  return (
                    <span className="flex items-center gap-2">
                      <span
                        aria-hidden
                        className="size-2 shrink-0 rounded-full"
                        style={{
                          background:
                            place === undefined
                              ? "transparent"
                              : pickColour(place),
                        }}
                      />
                      {row.sku ?? (
                        <span className="text-muted-foreground">No SKU</span>
                      )}
                    </span>
                  );
                },
              },
              {
                header: "Product",
                cell: (row) => (
                  <>
                    {row.productTitle}
                    {row.variantTitle ? (
                      <span className="text-muted-foreground block text-xs">
                        {row.variantTitle}
                      </span>
                    ) : null}
                  </>
                ),
              },
              {
                header: "Units",
                numeric: true,
                strong: true,
                cell: (row) => formatCount(row.units),
              },
              {
                header: "Orders",
                numeric: true,
                cell: (row) => formatCount(row.orders),
              },
              {
                header: "Sales",
                numeric: true,
                cell: (row) => formatMoney(row.amount),
              },
              {
                header: "Share of units",
                numeric: true,
                cell: (row) => shareText(row.units, series.totals.units),
              },
              {
                header: "Share of sales",
                numeric: true,
                cell: (row) =>
                  shareText(Number(row.amount), Number(series.totals.amount)),
              },
            ]}
          />
        </div>
        {rows.length > TABLE_ROWS ? (
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setAll((current) => !current);
              }}
            >
              {all
                ? `Show top ${String(TABLE_ROWS)}`
                : `Show all ${String(rows.length)}`}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
